import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COOKIE_KEY = 'clickone_cookie_consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_KEY, 'declined');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 sm:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-[#0A0A0A] border border-white/10 p-5 sm:p-6 shadow-2xl shadow-black/50">
            <div className="flex items-start gap-4">
              <Shield className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-white font-medium text-sm mb-2">Cookie Notice</h3>
                <p className="text-gray-400 text-xs leading-relaxed mb-4">
                  We use essential cookies to ensure our website functions properly and analytics cookies to understand how you interact with our site. You can accept all cookies or decline non-essential ones. For more information, see our{' '}
                  <Link to="/privacy-policy" className="text-[#E63946] hover:underline">Privacy Policy</Link>.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={handleAccept}
                    className="btn-primary-new px-5 py-2 rounded-none text-xs h-auto"
                    data-testid="cookie-accept-btn"
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={handleDecline}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 px-5 py-2 rounded-none text-xs h-auto"
                    data-testid="cookie-decline-btn"
                  >
                    Essential Only
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDecline}
                className="text-gray-500 hover:text-white transition-colors flex-shrink-0"
                data-testid="cookie-close-btn"
                aria-label="Close cookie banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
